import {
  Alert,
  AlertDescription,
  PageHeader,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  UserRoleBadge,
  Badge,
  errorMessage,
  useAuth,
  useUmClient,
} from "@nexys/user-management-web";
import { AlertCircle, Building2, KeyRound, LogOut, ShieldCheck, User } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export const DashboardPage = () => {
  const { data, signOut } = useAuth();
  const client = useUmClient();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [minting, setMinting] = useState(false);
  if (!data) return null;
  const { user, session } = data;

  const mint = async () => {
    setMinting(true);
    setTokenError(null);
    const res = await client.mintToken();
    setMinting(false);
    if (res.ok) setToken(res.data.accessToken);
    else setTokenError(errorMessage(res.error, "Couldn't mint an access token."));
  };

  const adminLink = (() => {
    if (user.role === "superadmin")
      return { href: "/users", label: "Manage users" };
    if (user.role === "useradmin")
      return { href: "/users", label: "Manage users" };
    return null;
  })();

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Dashboard"
        description="Account summary and quick links."
        actions={
          <Button variant="outline" onClick={() => void signOut()}>
            <LogOut /> Sign out
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="size-4 text-muted-foreground" /> Identity
            </CardTitle>
            <CardDescription>Who you're signed in as.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm">
            <Row label="Name" value={user.name ?? "—"} />
            <Row label="Email" value={user.email} testid="info-email" />
            <Row
              label="User role"
              value={<UserRoleBadge role={user.role} />}
            />
            <Row label="User ID" value={<code className="text-xs">{user.id}</code>} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="size-4 text-muted-foreground" /> Session
            </CardTitle>
            <CardDescription>Tenant context and access source.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm">
            <Row
              label="Auth source"
              value={<Badge variant="muted">{data.authSource}</Badge>}
            />
            <Row
              label="Active tenant"
              value={
                session.activeOrganizationId ? (
                  <code className="text-xs">{session.activeOrganizationId}</code>
                ) : (
                  <span className="text-muted-foreground">none</span>
                )
              }
            />
            <Row
              label="Tenant roles"
              value={
                session.tenantRoles.length === 0 ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {session.tenantRoles.map((r) => (
                      <Badge key={r} variant="muted">
                        {r}
                      </Badge>
                    ))}
                  </div>
                )
              }
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="size-4 text-muted-foreground" /> Access token
          </CardTitle>
          <CardDescription>
            Mint a short-lived JWT signed by the server's active key. Use it as
            a Bearer token against `/api/me`, `/api/admin/*`, etc.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3">
          <div>
            <Button onClick={() => void mint()} loading={minting}>
              <KeyRound /> Get access token
            </Button>
          </div>
          {tokenError ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription>{tokenError}</AlertDescription>
            </Alert>
          ) : null}
          {token ? (
            <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 text-xs">
              {token}
            </pre>
          ) : null}
        </CardContent>
      </Card>

      {adminLink ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="size-4 text-muted-foreground" /> Admin
            </CardTitle>
            <CardDescription>
              You have access to the admin surface.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate(adminLink.href)}>
              {adminLink.label}
            </Button>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

const Row = ({
  label,
  value,
  testid,
}: {
  label: string;
  value: React.ReactNode;
  testid?: string;
}) => (
  <div className="grid grid-cols-[120px_1fr] items-baseline gap-2">
    <span className="text-xs uppercase tracking-wide text-muted-foreground">
      {label}
    </span>
    <span id={testid} className="font-medium">
      {value}
    </span>
  </div>
);
